import React from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";

import {
  DetailDownloadControls,
  DetailRowsSection,
  DetailSearch,
  filterDetailRows,
  renderBackIcon,
} from "../../../shared/ui/detail-page/detail-page";
import * as DetailStyled from "../../../shared/ui/detail-page/detail-page.styled";
import { ROUTES } from "../../../system/router/routes";
import { getFlowsheetById } from "../model/flowsheets";

import * as Styled from "./flowsheets.styled";

interface IPatientInfoOutletContext {
  setIsStretchContent: React.Dispatch<React.SetStateAction<boolean>>;
}

export const FlowsheetDetail = () => {
  const navigate = useNavigate();
  const { patientID, flowsheetID } = useParams();
  const flowsheet = getFlowsheetById(flowsheetID);
  const outletContext = useOutletContext<
    IPatientInfoOutletContext | undefined
  >();
  const setIsStretchContent = outletContext?.setIsStretchContent;
  const [searchQuery, setSearchQuery] = React.useState("");

  React.useEffect(() => {
    setIsStretchContent?.(true);

    return () => {
      setIsStretchContent?.(false);
    };
  }, [setIsStretchContent]);

  const filteredRows = React.useMemo(
    () =>
      filterDetailRows(flowsheet.rows, searchQuery, (row) => [
        row.label,
        ...Object.values(row.values),
      ]),
    [flowsheet.rows, searchQuery],
  );

  const handleBack = () => {
    if (!patientID) {
      return;
    }

    navigate(
      ROUTES.PATIENT_DETAILS_FLOWSHEETS.replace(":patientID", patientID),
    );
  };

  return (
    <Styled.Container>
      <DetailStyled.BackBtn type="button" onClick={handleBack}>
        {renderBackIcon()}
        Back
      </DetailStyled.BackBtn>
      <DetailStyled.PageHeader>
        <Styled.Title>Flowsheet Details</Styled.Title>
        <DetailStyled.HeaderActions>
          <DetailDownloadControls
            fileName={`flowsheet-${flowsheetID ?? "details"}`}
          />
          <DetailSearch
            value={searchQuery}
            onChange={setSearchQuery}
            ariaLabel="Search flowsheet data"
          />
        </DetailStyled.HeaderActions>
      </DetailStyled.PageHeader>
      <DetailStyled.DetailLayout $singleColumn>
        <Styled.ContentScroller>
          <DetailStyled.EntityContainer>
            <DetailRowsSection id="flowsheet-details" title="Flowsheet Details">
              <DetailStyled.DetailRows>
                <DetailStyled.DetailRow>
                  <DetailStyled.Description>ID</DetailStyled.Description>
                  <DetailStyled.Value>
                    {flowsheet.flowsheetId}
                  </DetailStyled.Value>
                </DetailStyled.DetailRow>
                <DetailStyled.DetailRow>
                  <DetailStyled.Description>Name</DetailStyled.Description>
                  <DetailStyled.Value>{flowsheet.name}</DetailStyled.Value>
                </DetailStyled.DetailRow>
              </DetailStyled.DetailRows>
            </DetailRowsSection>

            <DetailRowsSection id="flowsheet-data" title="Flowsheet Data">
              <Styled.TableScroller>
                <Styled.FlowsheetTable>
                  <thead>
                    <tr>
                      <th aria-label="Flowsheet row" />
                      {flowsheet.columns.map((column) => (
                        <th key={column}>{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr key={row.label}>
                        <th scope="row">{row.label}</th>
                        {flowsheet.columns.map((column) => (
                          <td key={column}>{row.values[column] ?? "—"}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Styled.FlowsheetTable>
              </Styled.TableScroller>
            </DetailRowsSection>
          </DetailStyled.EntityContainer>
        </Styled.ContentScroller>
      </DetailStyled.DetailLayout>
    </Styled.Container>
  );
};
